/**
 * 详情页多印花裁剪结果磁贴解析。
 */

function trimOrEmpty(value) {
  return value == null ? '' : String(value).trim();
}

function readPrintIndex(print, fallbackIndex = 0) {
  const num = Number(print?.printIndex ?? print?.print_index);
  return Number.isFinite(num) ? num : fallbackIndex;
}

function hasPrintDetectMeta(print) {
  if (!print || typeof print !== 'object') return false;
  const bbox = print.bboxNormalized ?? print.bbox_normalized;
  return Boolean(
    trimOrEmpty(print.label)
    || (Array.isArray(bbox) && bbox.length === 4)
    || trimOrEmpty(print.croppedImageUrl ?? print.cropped_image_url),
  );
}

/**
 * 从详情行解析可展示的裁剪结果磁贴列表。
 * 多印花时展开 cropPosition.prints[]；仅有检测框/标签、尚未落盘的印花也展示为待裁剪磁贴。
 *
 * @param {{ imageId?: string, croppedUrl?: string, croppedDisplayUrl?: string, cropPosition?: { prints?: Array<object> } | null }} row
 * @returns {Array<{ tileKey: string, imageId: string, printIndex: number, url: string, displayUrl: string, label: string, shortLabel: string, hasSavedCrop?: boolean, isPrimary?: boolean }>}
 */
export function resolveCropResultTiles(row) {
  if (!row || typeof row !== 'object') return [];

  const imageId = trimOrEmpty(row.imageId);
  const prints = Array.isArray(row.cropPosition?.prints) ? row.cropPosition.prints : [];

  if (prints.length > 0) {
    const tiles = prints
      .map((print, index) => {
        if (!hasPrintDetectMeta(print)) return null;
        const url = trimOrEmpty(print.croppedImageUrl ?? print.cropped_image_url);
        const printIndex = readPrintIndex(print, index);
        const tileKey = `${imageId}-print-${printIndex}`;
        const shortLabel = trimOrEmpty(print.label) || `印花 ${printIndex + 1}`;
        const displayUrl = trimOrEmpty(print.croppedImageUrlDisplay ?? print.cropped_image_url_display) || url;
        return {
          tileKey,
          imageId: tileKey,
          printIndex,
          url,
          displayUrl,
          label: `裁剪 ${shortLabel}`,
          shortLabel,
          hasSavedCrop: Boolean(url),
        };
      })
      .filter(Boolean);

    if (tiles.length > 0) {
      return tiles;
    }
  }

  const croppedUrl = trimOrEmpty(row.croppedUrl);
  if (!croppedUrl) return [];

  return [{
    tileKey: `${imageId}-cropped`,
    imageId: `${imageId}-cropped`,
    printIndex: 0,
    url: croppedUrl,
    displayUrl: trimOrEmpty(row.croppedDisplayUrl) || croppedUrl,
    label: '裁剪结果',
    shortLabel: '',
    isPrimary: true,
  }];
}

/** 行是否有可展示的裁剪磁贴（含已检测但未落盘的印花） */
export function rowHasCropResultTiles(row) {
  return resolveCropResultTiles(row).length > 0;
}

/** 行是否至少有一处印花已保存裁剪图 */
export function rowHasSavedCropTiles(row) {
  return resolveCropResultTiles(row).some((tile) => tile.hasSavedCrop !== false && Boolean(trimOrEmpty(tile.url)));
}

/** 是否为多印花裁剪结果 */
export function rowHasMultipleCropResultTiles(row) {
  return resolveCropResultTiles(row).length > 1;
}

/**
 * 收集单张原图对应的全部可展示裁剪 URL（多印花时展开为多张）。
 * @param {{ status?: string, croppedUrl?: string, croppedDisplayUrl?: string, cropPosition?: object }} detail
 * @returns {string[]}
 */
export function collectCropOutputUrlsFromDetail(detail) {
  if (!detail || detail.status !== 'success') return [];
  const tiles = resolveCropResultTiles(detail);
  if (tiles.length > 0) {
    return tiles
      .filter((tile) => Boolean(trimOrEmpty(tile.url)))
      .map((tile) => trimOrEmpty(tile.displayUrl) || tile.url)
      .filter(Boolean);
  }
  const url = trimOrEmpty(detail.croppedDisplayUrl) || trimOrEmpty(detail.croppedUrl);
  return url ? [url] : [];
}

/**
 * 收集详情页「下载全部」条目（多印花时每张原图对应多处裁剪各一条）。
 *
 * @param {Array<object>} rows
 * @returns {Array<{ url: string, originalUrl?: string, carouselStem: string, templateStem: string, stampStem: string }>}
 */
export function collectCropDownloadItems(rows = []) {
  const items = [];

  (rows || []).forEach((row) => {
    if (!row || row.status !== 'success') return;
    const tiles = resolveCropResultTiles(row);
    if (tiles.length === 0) return;

    tiles.forEach((tile) => {
      if (!trimOrEmpty(tile.url)) return;
      items.push({
        url: tile.url,
        originalUrl: row.originalUrl,
        carouselStem: tiles.length > 1
          ? `裁剪_${row.imageId}_印花${tile.printIndex + 1}`
          : `裁剪_${row.imageId}`,
        templateStem: '',
        stampStem: '',
      });
    });
  });

  return items;
}

export function resolveCropTilePreview(row, tile, {
  croppedLoadFailed = {},
  referencePreviewByImageId = new Map(),
} = {}) {
  if (!row || !tile) {
    return { url: '', displayUrl: '', viewUrl: '', id: '', usesOriginal: false };
  }

  const imageId = trimOrEmpty(row.imageId);
  const useOriginal = Boolean(croppedLoadFailed[tile.tileKey] || croppedLoadFailed[imageId]);
  const url = useOriginal ? trimOrEmpty(row.originalUrl) : tile.url;
  const displayUrl = useOriginal
    ? (referencePreviewByImageId.get(imageId) || row.originalUrl || '')
    : (tile.displayUrl || tile.url || '');

  return {
    url,
    displayUrl,
    viewUrl: displayUrl,
    id: useOriginal ? `${imageId}-original` : tile.imageId,
    usesOriginal: useOriginal,
  };
}

const CROP_DETAIL_ORIGINAL_KEY_PREFIX = 'crop-detail-original:';
const CROP_DETAIL_RESULT_KEY_PREFIX = 'crop-detail-result:';

export function buildCropDetailOriginalSelectKey(imageId) {
  return `${CROP_DETAIL_ORIGINAL_KEY_PREFIX}${String(imageId)}`;
}

export function buildCropDetailResultSelectKey(tileKey) {
  return `${CROP_DETAIL_RESULT_KEY_PREFIX}${String(tileKey)}`;
}

function parseCropDetailOriginalSelectKey(key) {
  return String(key).slice(CROP_DETAIL_ORIGINAL_KEY_PREFIX.length);
}

function parseCropDetailResultSelectKey(key) {
  return String(key).slice(CROP_DETAIL_RESULT_KEY_PREFIX.length);
}

/** 单行已落盘的裁剪结果 selectKey（不含原图），按印花展示顺序 */
export function collectCropDetailResultKeysForRow(row) {
  const imageId = trimOrEmpty(row?.imageId);
  if (!imageId) return [];
  return resolveCropResultTiles(row)
    .filter((tile) => Boolean(trimOrEmpty(tile.url)))
    .map((tile) => buildCropDetailResultSelectKey(tile.tileKey));
}

/**
 * 按原图分组的裁剪结果 keys。
 * 每组对应一张原图下的印花，顺序与详情页「裁剪效果1/2/…」一致。
 */
export function collectCropDetailResultKeysGroupedBySource(rows = []) {
  return (rows || [])
    .map((row) => collectCropDetailResultKeysForRow(row))
    .filter((keys) => keys.length > 0);
}

/** 快速选中菜单项数：各组印花数的最大值（不含原图） */
export function getCropDetailQuickSelectPositionCount(rows = []) {
  const groups = collectCropDetailResultKeysGroupedBySource(rows);
  if (groups.length === 0) return 0;
  return groups.reduce((max, keys) => Math.max(max, keys.length), 0);
}

/**
 * 快速选中第 N 张：取每组原图第 N 个已完成印花（0-based）。
 * 例如三张原图各有 3/3/2 个印花时，第 1 张 = 各组第 1 个印花；不足 N 个的组跳过。
 */
export function collectCropDetailQuickSelectKeysAtPosition(rows = [], positionIndex = 0) {
  if (!Number.isFinite(positionIndex) || positionIndex < 0) return [];
  const picked = [];
  collectCropDetailResultKeysGroupedBySource(rows).forEach((keys) => {
    const key = keys[positionIndex];
    if (key) picked.push(key);
  });
  return picked;
}

/** 详情页所有可勾选磁贴的 selectKey（原图 + 裁剪结果） */
export function collectCropDetailSelectableTileKeys(rows = []) {
  const keys = [];
  (rows || []).forEach((row) => {
    const imageId = trimOrEmpty(row?.imageId);
    if (!imageId) return;
    if (trimOrEmpty(row.originalUrl)) {
      keys.push(buildCropDetailOriginalSelectKey(imageId));
    }
    collectCropDetailResultKeysForRow(row).forEach((key) => keys.push(key));
  });
  return keys;
}

/** 仅裁剪结果可勾选 keys（快速全选用不含原图） */
export function collectCropDetailResultSelectableTileKeys(rows = []) {
  return collectCropDetailResultKeysGroupedBySource(rows).flat();
}

export function resolveCropDetailSelectionEntry(selectKey, imageData = []) {
  const key = String(selectKey || '');
  if (key.startsWith(CROP_DETAIL_ORIGINAL_KEY_PREFIX)) {
    const imageId = parseCropDetailOriginalSelectKey(key);
    const row = imageData.find((img) => String(img.imageId) === imageId);
    const url = trimOrEmpty(row?.originalUrl);
    if (!url) return null;
    const displayUrl = trimOrEmpty(row?.originalDisplayUrl || row?.originalUrlDisplay);
    return {
      imageId,
      url,
      displayUrl,
      sourceId: `crop_original_${imageId}`,
      name: `原图_${imageId}`,
      carouselStem: `原图_${imageId}`,
    };
  }
  if (key.startsWith(CROP_DETAIL_RESULT_KEY_PREFIX)) {
    const tileKey = parseCropDetailResultSelectKey(key);
    for (const row of imageData) {
      const tile = resolveCropResultTiles(row).find((item) => item.tileKey === tileKey);
      if (!tile) continue;
      const imageId = String(row.imageId);
      const tiles = resolveCropResultTiles(row);
      return {
        imageId,
        url: tile.url,
        displayUrl: trimOrEmpty(tile.displayUrl),
        sourceId: `crop_${imageId}_${tile.tileKey}`,
        name: tile.label || `裁剪_${imageId}`,
        carouselStem: tiles.length > 1
          ? `裁剪_${imageId}_印花${tile.printIndex + 1}`
          : `裁剪_${imageId}`,
      };
    }
  }
  return null;
}

/** 图案裁剪详情 · 磁贴级多选 → 与 buildDetailSelectionPayload 相同结构 */
export function buildCropDetailTileSelectionPayload({ selectedTileKeys, imageData, taskId }) {
  const imageIds = [];
  const urlByImageId = {};
  const sources = [];
  const seenSourceIds = new Set();

  for (const selectKey of selectedTileKeys) {
    const entry = resolveCropDetailSelectionEntry(selectKey, imageData);
    if (!entry?.url || seenSourceIds.has(entry.sourceId)) continue;
    seenSourceIds.add(entry.sourceId);
    imageIds.push(entry.imageId);
    urlByImageId[entry.imageId] = entry.url;
    sources.push({
      id: entry.sourceId,
      name: entry.name,
      url: entry.url,
    });
  }

  return { imageIds, urlByImageId, sources, taskId };
}
